import type { ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  useCollectionBinding,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage, cn } from '@vibesites/ui-primitives';
import { amenityIcon } from '../utils/amenityIcon';
import { btnPrimary, clean, hasValue, linkTarget } from '../utils/content';

interface Amenity {
  title?: string | null;
}

const communityPhotoDef: SlotGroupDefinition = {
  label: 'Community Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[15].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[15].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 15, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'The best photo of a shared community amenity or common area here.',
    context: 'Beside the Community amenities list on the Features page. Different from the Apartment photo.',
  },
};

const apartmentPhotoDef: SlotGroupDefinition = {
  label: 'Apartment Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[16].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[16].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 16, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'The best interior photo of an apartment home (kitchen, living room, or bedroom).',
    context: 'Beside the Apartment features list on the Features page. Different from the Community photo.',
  },
};

const communityCtaDef: SlotGroupDefinition = {
  label: 'Community Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'See Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const apartmentCtaDef: SlotGroupDefinition = {
  label: 'Apartment Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'View Gallery' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

interface BlockProps {
  heading: ReactNode;
  items: Amenity[];
  photo: ReactNode;
  cta: ReactNode;
  reverse?: boolean;
  binding: (list: ReactNode) => ReactNode;
}

function AmenityBlock({ heading, items, photo, cta, reverse, binding }: BlockProps) {
  return (
    <div className="grid items-center gap-10 md:grid-cols-2 lg:gap-16">
      <div className={cn('order-1', reverse && 'md:order-2')}>{photo}</div>
      <div className={cn('order-2', reverse ? 'js-reveal-left md:order-1' : 'js-reveal-right')}>
        {heading}
        {binding(
          items.length > 0 ? (
            <ul className="js-stagger js-d2 mt-6 grid gap-x-8 gap-y-3.5 sm:grid-cols-2 md:grid-cols-1 xl:grid-cols-2">
              {items.map((a, i) => {
                const Icon = amenityIcon(a.title);
                return (
                  <li key={`${a.title}-${i}`} className="group flex items-start gap-3 font-body text-[16px] leading-snug text-foreground sm:text-[17px]">
                    <Icon className="mt-0.5 h-5 w-5 shrink-0 stroke-[1.5] text-primary transition-transform duration-500 ease-out group-hover:scale-125" aria-hidden />
                    <span>{clean(a.title)}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="js-copy mt-6 text-[color:var(--vibe-color-text-muted)]">Ask our team about everything included here.</p>
          ),
        )}
        {cta}
      </div>
    </div>
  );
}

export function AmenitiesList() {
  const communityHeading = useContentToken('amenitieslist.community_heading');
  const apartmentHeading = useContentToken('amenitieslist.apartment_heading');
  const community = useCollectionBinding<Amenity>('amenitieslist.community', { collection: 'amenities', defaultLimit: 18, filter: { group: 'Community' } });
  const apartment = useCollectionBinding<Amenity>('amenitieslist.apartment', { collection: 'amenities', defaultLimit: 18, filter: { group: 'Apartment' } });
  const communityPhoto = useSlotGroup('amenitieslist.community_photo', communityPhotoDef);
  const apartmentPhoto = useSlotGroup('amenitieslist.apartment_photo', apartmentPhotoDef);
  const communityCta = useSlotGroup('amenitieslist.community_cta', communityCtaDef);
  const apartmentCta = useSlotGroup('amenitieslist.apartment_cta', apartmentCtaDef);

  const tidy = (list: Amenity[] | null | undefined) => (list || []).filter((a) => hasValue(a.title));
  const headingClass = 'js-heading text-[34px] text-foreground sm:text-[40px] lg:text-[45px]';
  const photoBox = (p: Record<string, string | null>) => (
    <div className="js-reveal-img js-zoom aspect-[5/4] w-full overflow-hidden bg-muted">
      <VibesitesImage url={p.image || ''} alt={hasValue(p.alt) ? p.alt : ''} sizes="(min-width: 768px) 50vw, 100vw" className="h-full w-full object-cover" />
    </div>
  );

  return (
    <section className="bg-background py-12 md:py-16">
      <div className="mx-auto flex max-w-[1180px] flex-col gap-16 px-6 md:gap-24 lg:px-10">
        <AmenityBlock
          heading={
            <EditableToken path="amenitieslist.community_heading" as="h2" className={headingClass}>
              {communityHeading}
            </EditableToken>
          }
          items={tidy(community)}
          binding={(list) => (
            <EditableCollectionBinding bindingPath="amenitieslist.community" label="Community Amenities">
              {list}
            </EditableCollectionBinding>
          )}
          photo={
            <EditableSlotGroup groupPath="amenitieslist.community_photo" definition={communityPhotoDef}>
              {photoBox(communityPhoto)}
            </EditableSlotGroup>
          }
          cta={
            <EditableSlotGroup groupPath="amenitieslist.community_cta" definition={communityCtaDef}>
              <a href={communityCta.url || '/floor-plans'} target={linkTarget(communityCta.url, communityCta._target)} className={`${btnPrimary} mt-8`}>
                {communityCta.label || 'See Floor Plans'}
              </a>
            </EditableSlotGroup>
          }
        />
        <AmenityBlock
          reverse
          heading={
            <EditableToken path="amenitieslist.apartment_heading" as="h2" className={headingClass}>
              {apartmentHeading}
            </EditableToken>
          }
          items={tidy(apartment)}
          binding={(list) => (
            <EditableCollectionBinding bindingPath="amenitieslist.apartment" label="Apartment Features">
              {list}
            </EditableCollectionBinding>
          )}
          photo={
            <EditableSlotGroup groupPath="amenitieslist.apartment_photo" definition={apartmentPhotoDef}>
              {photoBox(apartmentPhoto)}
            </EditableSlotGroup>
          }
          cta={
            <EditableSlotGroup groupPath="amenitieslist.apartment_cta" definition={apartmentCtaDef}>
              <a href={apartmentCta.url || '/gallery'} target={linkTarget(apartmentCta.url, apartmentCta._target)} className={`${btnPrimary} mt-8`}>
                {apartmentCta.label || 'View Gallery'}
              </a>
            </EditableSlotGroup>
          }
        />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities & Features',
  tokens: {
    'amenitieslist.community_heading': { label: 'Community Heading', type: 'text', defaultValue: 'Community' },
    'amenitieslist.apartment_heading': { label: 'Apartment Heading', type: 'text', defaultValue: 'Apartment' },
  },
  slotGroups: {
    'amenitieslist.community_photo': communityPhotoDef,
    'amenitieslist.apartment_photo': apartmentPhotoDef,
    'amenitieslist.community_cta': communityCtaDef,
    'amenitieslist.apartment_cta': apartmentCtaDef,
  },
  collectionBindings: {
    'amenitieslist.community': {
      label: 'Community Amenities',
      collection: 'amenities',
      defaultLimit: 18,
      filter: { group: 'Community' },
    },
    'amenitieslist.apartment': {
      label: 'Apartment Features',
      collection: 'amenities',
      defaultLimit: 18,
      filter: { group: 'Apartment' },
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesList;
